import { useState, useEffect, useCallback } from 'react';
import { useApi } from './useApi';

export const useServices = () => {
  const { apiCall } = useApi();
  const [services, setServices] = useState([]);
  const [stats, setStats] = useState({
    total_services: 0,
    active_services: 0,
    service_ranking: []
  });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const fetchServices = useCallback(async (filters = {}) => {
    // Vérifier la disponibilité du token et des données utilisateur
    const token = localStorage.getItem('token');
    const user = JSON.parse(localStorage.getItem('user') || '{}');
    
    if (!token || !user.tenant_id) {
      console.log('⚠️ Token ou tenant_id manquant, impossible de charger les services');
      setServices([]);
      setLoading(false);
      return;
    }
    
    try {
      setLoading(true);
      setError(null);
      
      const queryParams = new URLSearchParams();
      Object.entries(filters).forEach(([key, value]) => {
        if (value && value !== 'Tous' && value !== '') {
          queryParams.append(key, value);
        }
      });

      const endpoint = `/services${queryParams.toString() ? `?${queryParams.toString()}` : ''}`;
      const data = await apiCall(endpoint);
      
      setServices(data.services || []);
      if (data.stats) {
        setStats(data.stats);
      }
    } catch (error) {
      console.error('Erreur lors du chargement des services:', error);
      setError(error.message);
      setServices([]);
    } finally {
      setLoading(false);
    }
  }, [apiCall]);

  const createService = useCallback(async (serviceData) => {
    try {
      const result = await apiCall('/services', {
        method: 'POST',
        body: JSON.stringify(serviceData)
      });
      await fetchServices(); // Recharger la liste
      return result;
    } catch (error) {
      console.error('Erreur lors de la création du service:', error);
      throw error;
    }
  }, [apiCall, fetchServices]);

  const updateService = useCallback(async (serviceId, serviceData) => {
    try {
      const result = await apiCall(`/services/${serviceId}`, {
        method: 'PUT',
        body: JSON.stringify(serviceData)
      });
      await fetchServices(); // Recharger la liste
      return result;
    } catch (error) {
      console.error('Erreur lors de la mise à jour du service:', error);
      throw error;
    }
  }, [apiCall, fetchServices]);

  const duplicateService = useCallback(async (serviceId, duplicateData) => {
    try {
      const result = await apiCall(`/services/${serviceId}/duplicate`, {
        method: 'POST',
        body: JSON.stringify(duplicateData)
      });
      await fetchServices(); // Recharger la liste
      return result;
    } catch (error) {
      console.error('Erreur lors de la duplication du service:', error);
      throw error;
    }
  }, [apiCall, fetchServices]);

  const toggleServiceStatus = useCallback(async (serviceId, is_active) => {
    try {
      const result = await apiCall(`/services/${serviceId}/status`, {
        method: 'PUT',
        body: JSON.stringify({ is_active })
      });
      await fetchServices(); // Recharger la liste
      return result;
    } catch (error) {
      console.error('Erreur lors du changement de statut:', error);
      throw error;
    }
  }, [apiCall, fetchServices]);

  const deleteService = useCallback(async (serviceId) => {
    try {
      const result = await apiCall(`/services/${serviceId}`, {
        method: 'DELETE'
      });
      await fetchServices(); // Recharger la liste
      return result;
    } catch (error) {
      console.error('Erreur lors de la suppression du service:', error);
      throw error;
    }
  }, [apiCall, fetchServices]);

  useEffect(() => {
    fetchServices();
  }, []);

  return {
    services,
    stats,
    loading,
    error,
    refetch: fetchServices,
    createService,
    updateService,
    duplicateService,
    toggleServiceStatus,
    deleteService
  };
};

export const useService = (id) => {
  const { apiCall } = useApi();
  const [service, setService] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const fetchService = useCallback(async () => {
    // Vérifier la disponibilité du token et des données utilisateur
    const token = localStorage.getItem('token');
    const user = JSON.parse(localStorage.getItem('user') || '{}');
    
    if (!token || !user.tenant_id) {
      console.log('⚠️ Token ou tenant_id manquant, impossible de charger le service');
      setLoading(false);
      return;
    }
    
    try {
      setLoading(true);
      setError(null);
      
      const data = await apiCall(`/services/${id}`);
      setService(data);
    } catch (error) {
      console.error('Erreur lors du chargement du service:', error);
      setError(error.message);
    } finally {
      setLoading(false);
    }
  }, [id, apiCall]);

  const updateService = useCallback(async (serviceData) => {
    try {
      const result = await apiCall(`/services/${id}`, {
        method: 'PUT',
        body: JSON.stringify(serviceData)
      });
      await fetchService(); // Recharger les données
      return result;
    } catch (error) {
      console.error('Erreur lors de la mise à jour du service:', error);
      throw error;
    }
  }, [id, apiCall, fetchService]);

  useEffect(() => {
    if (id) {
      fetchService();
    }
  }, [id]);

  return {
    service,
    loading,
    error,
    updateService,
    refetch: fetchService
  };
};

export const useServiceCategories = () => {
  const { apiCall } = useApi();
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const fetchCategories = useCallback(async () => {
    // Vérifier la disponibilité du token et des données utilisateur
    const token = localStorage.getItem('token');
    const user = JSON.parse(localStorage.getItem('user') || '{}');
    
    if (!token || !user.tenant_id) {
      console.log('⚠️ Token ou tenant_id manquant, impossible de charger les catégories');
      setCategories([]);
      setLoading(false);
      return;
    }
    
    try {
      setLoading(true);
      setError(null);
      
      const data = await apiCall('/services/categories');
      setCategories(data || []);
    } catch (error) {
      console.error('Erreur lors du chargement des catégories:', error);
      setError(error.message);
      setCategories([]);
    } finally {
      setLoading(false);
    }
  }, [apiCall]);

  useEffect(() => {
    fetchCategories();
  }, []);

  return {
    categories,
    loading,
    error,
    refetch: fetchCategories
  };
};